import { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import {
  OrderDeliveryResponse,
  ShippingType,
  OrdersDetailResponse,
  api,
  SHIPPING_TYPE,
} from '@/apis';
import { keys } from '@/hooks/queries/keys';
import { ORDER_TEMPLATE } from '@/utils';

import { useToast } from './useToast';
import { useDialog } from './useDialog';
/**
 * 배송 정보를 관리하기 위한 훅입니다.
 * @param order OrdersDetailResponse | undefined
 * @returns shippingInfo, setShippingInfo, updateShippingInfo, onInputShippingType
 */
export const useOrderShipping = (order: OrdersDetailResponse | undefined) => {
  const toastOn = useToast();
  const [dialogOn, dialogOff] = useDialog();
  const queryClient = useQueryClient();

  const [shippingInfo, setShippingInfo] = useState<OrderDeliveryResponse>({
    receiver_name: '',
    receiver_phone: '',
    receiver_address: '',
    receiver_detail_address: '',
    receiver_postcode: '',
    delivery_message: '',
    shipping_type: SHIPPING_TYPE.GENERAL,
  });
  const [showDeliveryModal, setShowDeliveryModal] = useState<boolean>(false);

  const onInputShippingType = (type: ShippingType) => {
    if (shippingInfo.shipping_type === type) {
      setShippingInfo({ ...shippingInfo, shipping_type: SHIPPING_TYPE.GENERAL });
      return;
    }
    setShippingInfo({ ...shippingInfo, shipping_type: type });
  };

  const updateShippingInfo = (order: OrdersDetailResponse | OrderDeliveryResponse) => {
    const {
      receiver_name,
      receiver_phone,
      receiver_address,
      receiver_detail_address,
      receiver_postcode,
      delivery_message,
      shipping_type,
    } = order;
    setShippingInfo({
      receiver_name,
      receiver_phone,
      receiver_address,
      receiver_detail_address,
      receiver_postcode,
      delivery_message,
      shipping_type,
    });
  };

  const onClickEditShippingModal = async (
    loggedIn: boolean,
    orderNumber: string,
    phone?: number,
  ) => {
    try {
      const { status } = loggedIn
        ? await api.orders.editDelivery(true, orderNumber, shippingInfo)
        : await api.orders.editDelivery(false, orderNumber, shippingInfo, phone);

      if (status === 204) {
        queryClient.refetchQueries(keys.orders.get(orderNumber));
        toastOn('배송 정보 수정 성공 😎');
        setShowDeliveryModal(false);
      }
    } catch (error) {
      if (isAxiosError(error)) {
        const { response } = error;
        const { message } = response?.data;
        if (typeof message === 'string') {
          dialogOn({
            template: ORDER_TEMPLATE.editDelivery.error(message),
            close: {
              text: '닫기',
              onClick: () => {
                dialogOff();
                if (order) updateShippingInfo(order);
              },
            },
          });
          return;
        }
      }
      dialogOn({
        template: ORDER_TEMPLATE.editDelivery.error(),
        close: {
          text: '닫기',
          onClick: () => {
            dialogOff();
            if (order) updateShippingInfo(order);
          },
        },
      });
    }
  };

  useEffect(() => {
    if (order) {
      updateShippingInfo(order);
    }
  }, [order]);

  return {
    shippingInfo,
    setShippingInfo,
    updateShippingInfo,
    onInputShippingType,
    showDeliveryModal,
    setShowDeliveryModal,
    onClickEditShippingModal,
  };
};
